import axios from 'axios';
import HttpService from '@/api/HttpService';

import {
  SET_DEFAULT_FUNDING_SOURCE,
  STOP_SET_DEFAULT_FUNDING_SOURCE,
  DISABLE_SUBMIT_SEARCH,
  ENABLE_SUBMIT_SEARCH,
  SET_NOTIFICATION_STATUS,
} from '../mutation-types';

export default {
  namespaced: true,
  state: {
    settingDefaultFundingSource: false,
    notificationStatus: 'init',
  },
  mutations: {
    [SET_DEFAULT_FUNDING_SOURCE](state) {
      state.settingDefaultFundingSource = true;
    },
    [STOP_SET_DEFAULT_FUNDING_SOURCE](state) {
      state.settingDefaultFundingSource = false;
    },
    [ENABLE_SUBMIT_SEARCH](state) {
      state.disableSubmitSearch = false;
    },
    [DISABLE_SUBMIT_SEARCH](state) {
      state.disableSubmitSearch = true;
    },
    [SET_NOTIFICATION_STATUS](state, value) {
      if (state.notificationStatus !== 'NONE' || value === 'init') {
        state.notificationStatus = value;
      }
    },
  },
  actions: {
    setDefaultFundingSource({ commit }, { accountId, fundingSourceId }) {
      commit(SET_DEFAULT_FUNDING_SOURCE);
      return HttpService.putV2(`accounts/${accountId}`, {
        defaultFundingSourceId: fundingSourceId,
      })
        .then((response) => {
          commit(STOP_SET_DEFAULT_FUNDING_SOURCE);
          return response;
        })
        .catch((error) => {
          commit(STOP_SET_DEFAULT_FUNDING_SOURCE);
          return Promise.reject(error);
        });
    },
    updateAccountPaymentPreference({ commit }, { accountId, paymentPreference }) {
      return HttpService.putV2(`/accounts/${accountId}`, { paymentPreference })
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    getAccount({ commit }, accountId) {
      return HttpService.getV2(`/accounts/${accountId}`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    getAccountUsers({ commit }, accountId) {
      return HttpService.getV2(`/accounts/${accountId}/users`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    getAccountEmails({ commit }, accountId) {
      return HttpService.getV2(`/accounts/${accountId}/emails`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    getAccountOrganizations({ commit }, accountId) {
      return HttpService.getV2(`/accounts/${accountId}/organizations`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    getAccountNotes({ commit }, accountId) {
      return HttpService.getV2(`/accounts/${accountId}/notes`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    getAccountNoteTransactions({ commit }, { accountId, noteId }) {
      return HttpService.getV2(`/accounts/${accountId}/notes/${noteId}/transactions`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    getAccountSummaryReport({ commit }, { accountId, withAccruedInterest }) {
      return HttpService.getV2(`/accounts/${accountId}/notes/summary-report?accrued=${withAccruedInterest.toString()}`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    async getDocumentFromS3({ commit }, { presignedUrl }) {
      return axios.get(presignedUrl, {
        responseType: 'arraybuffer',
      }).catch((err) => {
        throw err;
      });
    },
    getAccountImpactPreferences({ commit }, accountId, params = null) {
      return HttpService.getV2(`/accounts/${accountId}/impact-preferences`, params)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    deleteAccountUser({ commit }, { accountId, userId }) {
      return HttpService.deleteV2(`/accounts/${accountId}/users/${userId}`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    getAccountFundingSources({ commit }, accountId) {
      return HttpService.getV2(`/accounts/${accountId}/funding-sources`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    updateImpactPreferences({ commit }, { accountId, selectedPreferences }) {
      return HttpService.put(`accounts/${accountId}/impact-preferences`, selectedPreferences)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    updatePreferences({ commit }, { accountId, payload }) {
      return HttpService.put(`accounts/${accountId}/preferences`, payload)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    updateAccount({ commit }, { accountId, payload }) {
      return HttpService.putV2(`accounts/${accountId}`, payload)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    updateAccountPreferences({ commit }, { accountId, payload }) {
      return HttpService.putV2(`accounts/${accountId}/preferences`, payload);
    },
    updateAuthAccount({ commit }, { clientId, payload }) {
      return HttpService.putV2(`auth/${clientId}`, payload)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    updateAccountImpactPreferences({ commit }, { accountId, payload }) {
      return HttpService.putV2(`accounts/${accountId}/impact-preferences`, payload)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    getCurrentAmountBalance({ commit }, { accountId }) {
      return HttpService.getV2(`accounts/${accountId}/current-amount-balance`);
    },
    updateAccountName({ commit }, { accountId, name }) {
      return HttpService.putV2(`/accounts/${accountId}`, { accountName: name })
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    editInstitutionAccountInformation({ commit }, { params, payload }) {
      const { accountId, organizationId } = params;
      return HttpService.putV2(`/accounts/${accountId}/organizations/${organizationId}`, payload)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    editTrustAccountInformation({ commit }, { params, payload }) {
      const { accountId, organizationId } = params;
      return HttpService.putV2(`/accounts/${accountId}/trust/${organizationId}`, payload)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    getAccountTransactions({ commit }, { accountId, params }) {
      return HttpService.getV2(`/accounts/${accountId}/transactions`, params)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    deleteAccountEmail({ commit }, { accountId, email }) {
      return HttpService.deleteV2(`/accounts/${accountId}/emails`, { email })
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    addAccountEmail({ commit }, { accountId, email }) {
      return HttpService.postV2(`/accounts/${accountId}/emails`, { email })
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    createAccountUser({ commit }, { accountId, userId }) {
      return HttpService.postV2(`/accounts/${accountId}/users/${userId}`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    updateAccountPrimaryInvestor({ commit }, { accountId, userId }) {
      return HttpService.postV2(`accounts/${accountId}/primary-investor`, { userId })
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    getAccountDocuments({ commit }, { accountId, query }) {
      return HttpService.getV2(`accounts/${accountId}/documents`, query)
      .then(response => response)
      .catch((err) => {
        throw err;
      });
    },
    getSummaryPageDocuments({ commit }, { accountId }) {
      return HttpService.getV2(`accounts/${accountId}/documents/summary`)
      .then(response => response)
      .catch((err) => {
        throw err;
      });
    },
    createAccountDocumentPresignedUrl({ commit }, { accountId, payload }) {
      return HttpService.postV2(`accounts/${accountId}/documents/signed-url`, payload)
      .then(response => response)
      .catch((err) => {
        throw err;
      });
    },
    getAccountDocumentUrl({ commit }, { accountId, Key }) {
      return HttpService.get(`accounts/${accountId}/document`, { Key })
      .then(response => response)
      .catch((err) => {
        throw err;
      });
    },
    createDocumentView({ commit }, documentView) {
      return HttpService.postV2('documents-views', documentView)
      .then(response => response)
      .catch((err) => {
        throw err;
      });
    },
    getAccounts({ commit }, { searchString, searchBy }) {
      commit(DISABLE_SUBMIT_SEARCH);
      return HttpService.getV2('search', {
        searchString,
        searchBy,
      }).catch((err) => {
        throw err;
      })
        .then((response) => {
          commit(ENABLE_SUBMIT_SEARCH);
          return response;
        });
    },
    updateCommunicationPreferencesNotification({ commit }, accountId) {
      return HttpService.put(
        `accounts/${accountId}/communicationPreferencesUpdated`,
      )
        .then((response) => {
          commit(SET_NOTIFICATION_STATUS, response.data);
          return response.data;
        })
        .catch((err) => {
          throw err;
        });
    },
    setNotificationStatus({ commit }, value) {
      commit(SET_NOTIFICATION_STATUS, value);
    },
    getAccountBeneficiaries({ commit }, accountId) {
      return HttpService.getV2(`/accounts/${accountId}/beneficiaries`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    deleteAccountBeneficiary({ commit }, { accountId, beneficiaryId }) {
      return HttpService.deleteV2(`/accounts/${accountId}/beneficiaries/${beneficiaryId}`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    saveAccountBeneficiary({ commit }, { accountId, beneficiary }) {
      return HttpService.postV2(`/accounts/${accountId}/beneficiaries`, beneficiary)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    getAccountExtraReinvestments({ commit }, accountId) {
      return HttpService.getV2(`/accounts/${accountId}/extra-reinvestments`)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    createAccountExtraReinvestment({ commit }, { accountId, payload }) {
      return HttpService.postV2(`/accounts/${accountId}/extra-reinvestments`, payload)
        .then(response => response)
        .catch((err) => {
          throw err;
        });
    },
    cancelAccountExtraReinvestment({ commit }, { accountId, extraReinvestmentId }) {
      return HttpService.patchV2(
        `/accounts/${accountId}/extra-reinvestments/${extraReinvestmentId}/cancel`,
      )
        .then(response => response)
        .catch((err) => { throw err; });
    },
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/account.js